import { FormControl, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { t } from "i18next";
import { ReadOnlyInputEdit } from "./ReadOnlyInputEdit";
import {
  AccountSettingSchema,
  type AccountSettingSchemaType,
} from "@/schemas/accountSettings.schema";
import Country from "country-state-city/lib/country";
import State from "country-state-city/lib/state";
import { CountryCombobox } from "./CountryCombobox";
import { StateCombobox } from "./StateCombobox";
import { CityCombobox } from "./CityCombobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Occupation } from "@/common/types/occupation";
import { SingleFormDialog } from "./SingleFormDialog";

interface Props {
  onSubmit: (data: Partial<AccountSettingSchemaType>) => void;
  defaultValues: AccountSettingSchemaType;
  occupations: Occupation[];
  loadingOccupations: boolean;
}

export const ProfileForm = (props: Props): JSX.Element => {
  return (
    <div className="flex flex-col gap-4">
      <SingleFormDialog
        defaultValues={{ firstname: props.defaultValues.firstname }}
        name={"firstname"}
        schema={AccountSettingSchema().pick({ firstname: true })}
        title={`${t("settings.profile.editModal")} ${t("settings.profile.firstName")}`}
        render={(field) => (
          <FormItem>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
        trigger={
          <ReadOnlyInputEdit
            label={t("settings.profile.firstName")}
            value={props.defaultValues.firstname}
          />
        }
        onSubmit={(value) => {
          props.onSubmit({ firstname: value.firstname });
        }}
      />
      <SingleFormDialog
        defaultValues={{ lastname: props.defaultValues.lastname }}
        name={"lastname"}
        schema={AccountSettingSchema().pick({ lastname: true })}
        title={`${t("settings.profile.editModal")} ${t("settings.profile.lastName")}`}
        render={(field) => (
          <FormItem>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
        trigger={
          <ReadOnlyInputEdit
            label={t("settings.profile.lastName")}
            value={props.defaultValues.lastname}
          />
        }
        onSubmit={(value) => {
          props.onSubmit({ lastname: value.lastname });
        }}
      />
      <SingleFormDialog
        defaultValues={{ phone: props.defaultValues.phone }}
        name={"phone"}
        schema={AccountSettingSchema().pick({ phone: true })}
        title={`${t("settings.profile.editModal")} ${t("settings.profile.phone")}`}
        render={(field) => (
          <FormItem>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
        trigger={
          <ReadOnlyInputEdit
            label={t("settings.profile.phone")}
            value={props.defaultValues.phone}
          />
        }
        onSubmit={(value) => {
          props.onSubmit({ phone: value.phone });
        }}
      />
      <SingleFormDialog
        name={"occupation"}
        schema={AccountSettingSchema().pick({ occupation: true })}
        title={`${t("settings.profile.editModal")} ${t("settings.profile.phone")}`}
        defaultValues={{
          occupation: props.defaultValues.occupation,
        }}
        render={(field) => (
          <FormItem>
            <Select
              disabled={props.loadingOccupations}
              value={field.value}
              onValueChange={(value) => {
                const selected = props.occupations.find(
                  (occ) => occ.occupation === value
                );
                if (selected) {
                  field.onChange(selected.occupation);
                }
              }}
            >
              <SelectTrigger id={field.name}>
                <SelectValue
                  placeholder={t("signUp.form.occupationPlaceholder")}
                />
              </SelectTrigger>
              <SelectContent>
                {props.occupations.map((occ) => (
                  <SelectItem key={occ.id} value={occ.occupation}>
                    {occ.occupation}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
        trigger={
          <ReadOnlyInputEdit
            label={t("settings.profile.occupation")}
            value={props.defaultValues.occupation}
          />
        }
        onSubmit={(value) => {
          props.onSubmit({
            occupation: value.occupation,
            occupationId: props.occupations.find(
              (occ) => occ.occupation === value.occupation
            )?.id,
          });
        }}
      />
      <SingleFormDialog
        name={"country"}
        schema={AccountSettingSchema().pick({ country: true })}
        title={`${t("settings.profile.editModal")} ${t("settings.profile.occupation")}`}
        defaultValues={{
          country: props.defaultValues.country,
        }}
        render={(field) => (
          <FormItem>
            <FormControl>
              <CountryCombobox
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value.value);
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
        trigger={
          <ReadOnlyInputEdit
            label={t("settings.profile.country")}
            value={
              Country.getCountryByCode(props.defaultValues.country)?.name || ""
            }
          />
        }
        onSubmit={(value) => {
          props.onSubmit({
            country: value.country,
          });
        }}
      />
      <SingleFormDialog
        name={"state"}
        schema={AccountSettingSchema().pick({ state: true })}
        title={`${t("settings.profile.editModal")} ${t("settings.profile.occupation")}`}
        defaultValues={{
          state: props.defaultValues.state,
        }}
        render={(field) => (
          <FormItem>
            <FormControl>
              <StateCombobox
                countryCode={props.defaultValues.country}
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value.value);
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
        trigger={
          <ReadOnlyInputEdit
            label={t("settings.profile.state")}
            value={
              State.getStateByCodeAndCountry(
                props.defaultValues.state,
                props.defaultValues.country
              )?.name ?? ""
            }
          />
        }
        onSubmit={(value) => {
          props.onSubmit({
            state: value.state,
          });
        }}
      />
      <SingleFormDialog
        name={"city"}
        schema={AccountSettingSchema().pick({ city: true })}
        title={`${t("settings.profile.editModal")} ${t("settings.profile.city")}`}
        defaultValues={{
          city: props.defaultValues.city,
        }}
        render={(field) => (
          <FormItem>
            <FormControl>
              <CityCombobox
                countryCode={props.defaultValues.country}
                stateCode={props.defaultValues.state}
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value.value);
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
        trigger={
          <ReadOnlyInputEdit
            label={t("settings.profile.city")}
            value={props.defaultValues.city}
          />
        }
        onSubmit={(value) => {
          props.onSubmit({
            city: value.city,
          });
        }}
      />
    </div>
  );
};
